import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { en, type MessageKey } from './messages';

export type { MessageKey } from './messages';
export type Vars = Record<string, string | number>;
type Dict = Record<string, Record<string, string>>;

export const LOCALES = { en } as const;
export type Locale = keyof typeof LOCALES;

/**
 * Looks a key up and fills `{name}` placeholders. Plurals: pass `count` in vars and
 * define `key_one` / `key_other` (Intl.PluralRules categories); the bare key is the fallback.
 * A missing key renders the key itself, so a gap is visible rather than blank.
 */
export function translate(locale: Locale, key: MessageKey | string, vars?: Vars): string {
  const dict = LOCALES[locale] as unknown as Dict;
  const dot = key.indexOf('.');
  const ns = dict[key.slice(0, dot)];
  const k = key.slice(dot + 1);
  let msg: string | undefined;
  if (ns && vars && typeof vars.count === 'number') {
    const cat = new Intl.PluralRules(locale).select(vars.count);
    msg = ns[`${k}_${cat}`] ?? ns[`${k}_other`];
  }
  msg ??= ns?.[k];
  if (msg === undefined) {
    if (import.meta.env.DEV) console.warn(`[i18n] missing key ${key}`);
    return key;
  }
  return vars ? msg.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m)) : msg;
}

export type T = (key: MessageKey, vars?: Vars) => string;

const I18nContext = createContext<{ locale: Locale; t: T }>({
  locale: 'en',
  t: (key, vars) => translate('en', key, vars),
});

export function I18nProvider({ locale = 'en', children }: { locale?: Locale; children: ReactNode }) {
  const value = useMemo(() => ({ locale, t: ((key, vars) => translate(locale, key, vars)) as T }), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** `const t = useT(); t('S1.title')` */
export function useT(): T {
  return useContext(I18nContext).t;
}

export function useLocale(): Locale {
  return useContext(I18nContext).locale;
}
